import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  Timestamp,
  DocumentSnapshot,
} from 'firebase/firestore';

import { db } from '../config/firebase';
import { UserRole } from '../types/auth';

/**
 * @file users.ts
 * @description Servicio unificado para gestión de usuarios (consolidación de admin/users.ts y firestore/users.ts)
 */

// Tipos unificados
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: UserRole;
  createdAt: Timestamp | Date;
  lastLogin: Timestamp | Date;
  isActive: boolean;
  webIds: string[];
  subscription?: {
    plan: 'freemium' | 'premium' | 'familiar';
    status: 'active' | 'inactive' | 'cancelled';
    expiresAt?: Timestamp;
  };
  settings?: {
    notifications: boolean;
    language: string;
    theme: 'light' | 'dark';
  };
}

export interface UserListOptions {
  limit?: number;
  orderBy?: 'createdAt' | 'lastLogin' | 'email' | 'displayName';
  orderDirection?: 'asc' | 'desc';
  role?: UserRole;
  isActive?: boolean;
  lastDoc?: DocumentSnapshot;
}

// Referencia a la colección de usuarios
const usersCollection = collection(db, 'users');

/**
 * Crear un nuevo usuario en Firestore
 * Función unificada que combina funcionalidad de admin y firestore
 */
export const createUser = async (
  uid: string, 
  userData: Partial<UserProfile> & { email: string; displayName: string }
): Promise<void> => {
  try {
    // Verificar que el email no esté en uso (solo para creación administrativa)
    if (userData.role === 'admin') {
      const emailQuery = query(usersCollection, where('email', '==', userData.email));
      const existingUsers = await getDocs(emailQuery);

      if (!existingUsers.empty) {
        throw new Error('El email ya está registrado en el sistema');
      }
    }

    const userRef = doc(usersCollection, uid);
    const userDoc: UserProfile = {
      uid,
      email: userData.email,
      displayName: userData.displayName,
      photoURL: userData.photoURL || null,
      role: userData.role || 'user',
      createdAt: serverTimestamp() as Timestamp,
      lastLogin: serverTimestamp() as Timestamp,
      isActive: userData.isActive ?? true,
      webIds: userData.webIds || [],
      subscription: userData.subscription || {
        plan: 'freemium',
        status: 'active',
      },
      settings: userData.settings || {
        notifications: true,
        language: 'es',
        theme: 'light',
      },
    };
    
    await setDoc(userRef, userDoc);
    console.log('✅ Usuario creado en Firestore:', uid);
  } catch (error) {
    console.error('❌ Error creando usuario:', error);
    throw error;
  }
};

/**
 * Crear usuario administrativo (genera UID temporal)
 * Para casos donde se crea desde panel admin sin Firebase Auth
 */
export const createAdminUser = async (userData: {
  email: string;
  displayName: string;
  role: UserRole;
  isActive?: boolean;
}): Promise<string> => {
  try {
    // Generar un UID temporal (en producción esto vendría de Firebase Auth)
    const tempUid = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    await createUser(tempUid, userData);
    return tempUid;
  } catch (error) {
    console.error('❌ Error creando usuario administrativo:', error);
    throw error;
  }
};

/**
 * Obtener un usuario por UID
 */
export const getUser = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userRef = doc(usersCollection, uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data() as UserProfile;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error obteniendo usuario:', error);
    throw error;
  }
};

/**
 * Obtener lista de usuarios con filtros y paginación
 */
export const getUsers = async (options: UserListOptions = {}): Promise<{
  users: UserProfile[];
  lastDoc?: DocumentSnapshot;
  hasMore: boolean;
}> => {
  try {
    const {
      limit: limitCount = 50,
      orderBy: orderField = 'createdAt',
      orderDirection = 'desc',
      role,
      isActive,
      lastDoc,
    } = options;

    let q = query(
      usersCollection,
      orderBy(orderField, orderDirection),
      limit(limitCount + 1) // +1 para verificar si hay más
    );

    // Aplicar filtros
    if (role) {
      q = query(q, where('role', '==', role));
    }
    
    if (isActive !== undefined) {
      q = query(q, where('isActive', '==', isActive));
    }

    // Paginación
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    
    // Verificar si hay más documentos
    const hasMore = docs.length > limitCount;
    const users = docs.slice(0, limitCount).map(doc => ({
      ...doc.data(),
      uid: doc.id,
    })) as UserProfile[];

    return {
      users,
      lastDoc: hasMore ? docs[limitCount - 1] : undefined,
      hasMore,
    };
  } catch (error) {
    console.error('❌ Error obteniendo usuarios:', error);
    throw new Error('Error al obtener lista de usuarios');
  }
};

/**
 * Actualizar un usuario
 */
export const updateUser = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  try {
    const userRef = doc(usersCollection, uid);
    await updateDoc(userRef, {
      ...updates,
      lastLogin: serverTimestamp(),
    });
    console.log('✅ Usuario actualizado:', uid);
  } catch (error) {
    console.error('❌ Error actualizando usuario:', error);
    throw error;
  }
};

/**
 * Eliminar un usuario
 */
export const deleteUser = async (uid: string): Promise<void> => {
  try {
    const userRef = doc(usersCollection, uid);
    await deleteDoc(userRef);
    console.log('✅ Usuario eliminado:', uid);
  } catch (error) {
    console.error('❌ Error eliminando usuario:', error);
    throw error;
  }
};

/**
 * Buscar usuarios por email o nombre
 */
export const searchUsers = async (searchTerm: string): Promise<UserProfile[]> => {
  try {
    // TODO: Implementar búsqueda full-text con Algolia o similar
    // Por ahora, obtener todos y filtrar en cliente
    const { users } = await getUsers({ limit: 1000 });

    const filtered = users.filter(user =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered;
  } catch (error) {
    console.error('❌ Error buscando usuarios:', error);
    throw new Error('Error al buscar usuarios');
  }
};

/**
 * Obtener estadísticas de usuarios
 */
export const getUserStats = async (): Promise<{
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  newUsersThisMonth: number;
}> => {
  try {
    // TODO: Implementar consultas agregadas o usar Cloud Functions
    // Por ahora, obtener todos los usuarios y calcular estadísticas
    const { users } = await getUsers({ limit: 1000 });

    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.isActive).length;
    const adminUsers = users.filter(u => u.role === 'admin').length;

    const thisMonth = new Date();
    thisMonth.setDate(1);
    thisMonth.setHours(0, 0, 0, 0);

    const newUsersThisMonth = users.filter(u =>
      u.createdAt && new Date(u.createdAt as any) >= thisMonth
    ).length;

    return {
      totalUsers,
      activeUsers,
      adminUsers,
      newUsersThisMonth,
    };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas de usuarios:', error);
    throw new Error('Error al obtener estadísticas de usuarios');
  }
};

/**
 * Actualizar rol de usuario
 */
export const updateUserRole = async (uid: string, role: UserRole): Promise<void> => {
  try {
    const userRef = doc(usersCollection, uid);
    await updateDoc(userRef, { role });
    console.log('✅ Rol de usuario actualizado:', uid, role);
  } catch (error) {
    console.error('❌ Error actualizando rol de usuario:', error);
    throw new Error('Error al actualizar rol de usuario');
  }
};

/**
 * Alternar estado activo/inactivo de usuario
 */
export const toggleUserStatus = async (uid: string, newStatus?: boolean): Promise<void> => {
  try {
    let targetStatus = newStatus;

    // Si no se proporciona newStatus, obtener el usuario y alternar
    if (targetStatus === undefined) {
      const user = await getUser(uid);
      if (!user) {
        throw new Error('Usuario no encontrado');
      }
      targetStatus = !user.isActive;
    }

    const userRef = doc(usersCollection, uid);
    await updateDoc(userRef, {
      isActive: targetStatus,
      lastLogin: serverTimestamp(),
    });
    console.log('✅ Estado de usuario actualizado:', uid, targetStatus);
  } catch (error) {
    console.error('❌ Error actualizando estado de usuario:', error);
    throw new Error('Error al actualizar estado de usuario');
  }
};

// Alias para compatibilidad con código existente
export type AdminUserData = UserProfile;
